import { useRef, useEffect, useContext, useState } from "react";
import { loadModules } from "esri-loader";
import MapContext from "../../context/MapContext";
import styles from "./CollectorMap.module.css";
import StatusPrompt from "../prompts/StatusPrompt";
import AuthContext from "../../context/AuthContext";
import clipboardSVG from "../../../assets/clipboard.svg";
import useAuditLocate from "./hooks/useAuditLocate";
import useBrowserStorage from "./hooks/useBrowserStorage";
import useMapInitializer from "./hooks/useMapInitializer";
import useMapMethods from "./hooks/useMapMethods";
import useMapQueries from "./hooks/useMapQueries";
import usePopupAction from "./hooks/usePopupAction";
import { useNavigate, useLocation } from "react-router-dom";

const CollectorMap = ({ userLocation }) => {
  // State.
  const mapContext = useContext(MapContext);
  const authCtx = useContext(AuthContext);
  const mapRef = useRef(null);
  const [noPhotoPoint, setNoPhotoPoint] = useState(false);
  const [readonlyPrompt, setReadonlyPrompt] = useState(false);

  // Sets a value for the latitude and longitude passed to the map
  // from collect.
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [routeCoords] = useState(routeLocation.state);

  // Imported business logic.
  // Popup actions.
  const handleViewPopupAction = usePopupAction();

  // Map initializer.
  const { addProxyRule, addESRIWidgets, createFeatureLayers } =
    useMapInitializer();

  // Map queries.
  const { getClosestFeatures, queryFeatureLayer, queryPhotoPointByID } =
    useMapQueries();

  // Audit locate.
  const addAuditLocate = useAuditLocate();

  // Map methods.
  const {
    addFeatureLayersToMap,
    renderGreenfieldGraphic,
    showAuditButton,
    watchLayerVisibility,
  } = useMapMethods();

  // Browser storage.
  const { saveLayerVisibility, saveBasemapValue } =
    useBrowserStorage(mapContext);

  /* 
    If the selected popup has a THREEGISID, it is a feature and
    the photo point can be fetched using it. If not, it is a
    greenfield photo point and the attached photo point can
    be fetched using a spacial query on the photo point
    layer within x distance of the popup point.
  */
  const determinePhotoPoint = async ({
    attributes,
    featureLayers,
    geometry,
  }) => {
    if (attributes?.THREEGISID) {
      return await queryPhotoPointByID(
        featureLayers.photoPointLayer,
        attributes.THREEGISID
      );
    } else {
      return await queryFeatureLayer({
        layer: featureLayers.photoPointLayer,
        geometry: geometry,
        distance: 30,
      });
    }
  };

  const determineAccountcode = async ({
    attributes,
    featureLayers,
    geometry,
  }) => {
    if (attributes?.ACCOUNTCODE) {
      return attributes.ACCOUNTCODE;
    } else {
      return await getClosestFeatures({
        geometry,
        featureLayers,
        distance: 2500,
      });
    }
  };

  // Map creation on component load.
  useEffect(() => {
    // Module order must correspond to the .then array.
    loadModules(
      [
        "esri/views/MapView",
        "esri/Map",
        "esri/layers/FeatureLayer",
        "esri/widgets/Search",
        "esri/widgets/Legend",
        "esri/widgets/Expand",
        "esri/widgets/BasemapGallery",
        "esri/widgets/LayerList",
        "esri/widgets/Locate",
        "esri/Graphic",
        "esri/widgets/Track",
        "esri/widgets/Compass",
        "esri/core/urlUtils",
        "esri/Basemap",
        "esri/layers/GraphicsLayer",
        "esri/core/watchUtils",
        "esri/widgets/ScaleBar",
      ],
      {
        css: true,
      }
    ).then(
      ([
        MapView,
        Map,
        FeatureLayer,
        Search,
        Legend,
        Expand,
        BasemapGallery,
        LayerList,
        Locate,
        Graphic,
        Track,
        Compass,
        urlUtils,
        Basemap,
        GraphicsLayer,
        watchUtils,
        ScaleBar,
      ]) => {
        addProxyRule(urlUtils);

        // Initialze map and view.
        const latitudeCoord = routeCoords?.latitude
          ? routeCoords.latitude
          : userLocation.latitude;
        const longitudeCoord = routeCoords?.longitude
          ? routeCoords.longitude
          : userLocation.longitude;
        const map = new Map({ basemap: mapContext.basemap });
        const view = new MapView({
          map: map,
          center: [longitudeCoord, latitudeCoord],
          zoom: 18,
          container: mapRef.current,
        });

        // Enable auto-close on the popups when the view is changed.
        view.popup.autoCloseEnabled = true;

        // Initialize feature layers.
        const featureLayers = createFeatureLayers({
          FeatureLayer: FeatureLayer,
          mapContext: mapContext,
          GraphicsLayer: GraphicsLayer,
          authCtx: authCtx,
        });

        // Add ESRI widgets to the map.
        addESRIWidgets({
          Basemap,
          BasemapGallery,
          Compass,
          Expand,
          featureLayers,
          Graphic,
          LayerList,
          Legend,
          Locate,
          ScaleBar,
          Search,
          Track,
          view,
        });

        // Add the feature layers to the map.
        addFeatureLayersToMap({ featureLayers, map });

        // Add custom button action for auditing to the map.
        addAuditLocate({
          Locate,
          view,
          featureLayers,
          Graphic,
        });

        // Maintain layer visibility state in browser storage.
        watchLayerVisibility({
          watchUtils,
          featureLayers: featureLayers,
          saveLayerVisibility,
        });

        // Maintain basemap layer state in browser storage.
        map.watch("basemap.title", (basemap) => {
          saveBasemapValue(basemap);
        });

        // View's on click listener for rendering the greenfield graphic.
        view.on("click", async (event) => {
          await renderGreenfieldGraphic({
            event,
            featureLayers,
            Graphic,
            view,
          });
        });

        // Watch for any popup close to remove the greenfield graphic.
        watchUtils.whenTrue(view.popup, "visible", () => {
          watchUtils.whenFalseOnce(view.popup, "visible", () => {
            featureLayers.graphicsLayer.removeAll();
          });
        });

        // Popup action button listener.
        watchUtils.whenOnce(view, "ready").then(() => {
          showAuditButton();

          // Event handler that fires each time an action is clicked
          view.popup.on("trigger-action", async (event) => {
            let feature = view?.popup?.selectedFeatureWidget;
            let geometry = feature?.graphic?.geometry;
            let attributes = feature?.graphic?.attributes;

            if (event?.action?.id === "contractorAudit") {
              navigate("/contractorAudit", {
                replace: true,
                state: {
                  latitude: geometry.latitude,
                  longitude: geometry.longitude,
                },
              });
              return;
            }

            if (event?.action?.id === "splicerAudit") {
              navigate("/splicerAudit", {
                replace: true,
                state: {
                  latitude: geometry.latitude,
                  longitude: geometry.longitude,
                },
              });
              return;
            }

            let photoPoint = await determinePhotoPoint({
              attributes,
              featureLayers,
              geometry,
            });
            if (photoPoint) {
              photoPoint.photoPointLayer = featureLayers.photoPointLayer;
            }

            let accountcode = await determineAccountcode({
              attributes,
              featureLayers,
              geometry,
            });

            // Process the action button event.
            handleViewPopupAction({
              accountcode,
              attributes,
              authCtx,
              ID: event.action.id,
              location: {
                latitude: geometry.latitude,
                longitude: geometry.longitude,
              },
              photoPoint,
              photoPointLayer: featureLayers.photoPointLayer,
              setNoPhotoPoint,
              setReadonlyPrompt,
              userLocation,
            });
          });
        });
      }
    );

    // Cleanup.
    return () => {
      window.location.reload();
    };
    //eslint-disable-next-line
  }, []);

  return (
    <div className={styles.mapDiv} ref={mapRef}>
      <link
        rel="stylesheet"
        href="https://js.arcgis.com/4.22/esri/themes/dark/main.css"
      />

      <button className={styles.mapButton} id="auditButton">
        <img alt="audit" src={clipboardSVG} />
      </button>

      {noPhotoPoint && (
        <StatusPrompt
          text="Selected feature has no photo point."
          isActive={true}
          statusCleared={() => setNoPhotoPoint(false)}
        />
      )}
      {readonlyPrompt && (
        <StatusPrompt
          text="Account is read-only."
          isActive={true}
          statusCleared={() => setReadonlyPrompt(false)}
        />
      )}
    </div>
  );
};

export default CollectorMap;
